import React, { useCallback, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import Papa from 'papaparse';
import { validate as isValidUUID, v4 as uuidv4 } from 'uuid';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';

import { useBatchedBulkDeleteVoucherUsers } from '~/hooks/apis/promo/useBulkDeleteVoucherUsers';
import { allVoucherUsersQueryKeys } from '~/hooks/apis/promo/useListAllVoucherUsers';

import { BatchedBulkDeleteVoucherUsersResponse } from '~/services/PromoService';

const BATCH_SIZE = 200;
const CSV_HEADERS = ['user_id', 'userId', 'customer_id', 'customerId'];

type Props = {
  open: boolean;
  onClose: () => void;
  voucherId: string;
  voucherName: string;
};

type Review = {
  requestedCount: number;
  eligibleCount: number;
  skippedCount: number;
  deletedCount: number;
  voucherName: string;
};

const EMPTY_REVIEW: Omit<Review, 'voucherName'> = {
  requestedCount: 0,
  eligibleCount: 0,
  skippedCount: 0,
  deletedCount: 0,
};

type ResultRow = {
  id: string;
  batchRunCount: number;
  userId: string;
  voucherUserId?: string;
  outcome: 'will_delete' | 'deleted' | 'skipped';
  reason?: string;
};

const RESULT_COLUMNS: Array<GridColDef<ResultRow>> = [
  { field: 'batchRunCount', headerName: 'Batch', width: 90 },
  { field: 'userId', headerName: 'User ID', flex: 1, minWidth: 260 },
  { field: 'voucherUserId', headerName: 'Voucher user ID', flex: 1, minWidth: 260 },
  { field: 'outcome', headerName: 'Result', width: 130 },
  { field: 'reason', headerName: 'Reason', flex: 1, minWidth: 180 },
];

function responseRows(response: BatchedBulkDeleteVoucherUsersResponse, dryRun: boolean): Array<ResultRow> {
  const { result } = response;
  const successfulRows = dryRun ? result.willDelete : result.deletedVoucherUsers;

  return [
    ...successfulRows.map((voucherUser) => ({
      id: `${result.batchId ?? result.batchRunCount}-${voucherUser.userId}`,
      batchRunCount: result.batchRunCount ?? 0,
      userId: voucherUser.userId,
      voucherUserId: voucherUser.voucherUserId,
      outcome: dryRun ? ('will_delete' as const) : ('deleted' as const),
    })),
    ...result.skipped.map((skipped) => ({
      id: `${result.batchId ?? result.batchRunCount}-${skipped.userId}`,
      batchRunCount: result.batchRunCount ?? 0,
      userId: skipped.userId,
      outcome: 'skipped' as const,
      reason: skipped.reason.replace(/_/g, ' '),
    })),
  ];
}

function formatCount(count: number): string {
  return count.toLocaleString();
}

export default function BulkDeleteVoucherUsersDialog({ open, onClose, voucherId, voucherName }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const bulkDeleteMutation = useBatchedBulkDeleteVoucherUsers();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState('');
  const [userIds, setUserIds] = useState<Array<string>>([]);
  const [invalidCount, setInvalidCount] = useState(0);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [progress, setProgress] = useState<number | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState<Review | null>(null);
  const [results, setResults] = useState<Array<ResultRow>>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [isRunning, setIsRunning] = useState(false);

  const reset = useCallback(() => {
    setFileName('');
    setUserIds([]);
    setInvalidCount(0);
    setDuplicateCount(0);
    setProgress(null);
    setReview(null);
    setError('');
    setCompleted(null);
    setResults([]);
    setPaginationModel({ page: 0, pageSize: 25 });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleClose = useCallback(() => {
    if (!isRunning) {
      reset();
      onClose();
    }
  }, [isRunning, onClose, reset]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setFileName(file.name);
    setReview(null);
    setCompleted(null);
    setError('');
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (headerResult) => {
        const header = headerResult.meta.fields?.find((field) => CSV_HEADERS.includes(field));
        const setParsedUserIds = (rawUserIds: Array<string>) => {
          const trimmedUserIds = rawUserIds.map((value) => String(value).trim()).filter(Boolean);
          const uniqueUserIds = Array.from(new Set(trimmedUserIds));
          const validUserIds = uniqueUserIds.filter(isValidUUID);
          setUserIds(validUserIds);
          setDuplicateCount(trimmedUserIds.length - uniqueUserIds.length);
          setInvalidCount(uniqueUserIds.length - validUserIds.length);
        };
        if (header) {
          setParsedUserIds(headerResult.data.map((row) => row[header] ?? ''));
          return;
        }
        Papa.parse<Array<string>>(file, {
          skipEmptyLines: true,
          complete: (noHeaderResult) => {
            const rawUserIds = noHeaderResult.data.map((row) => row[0] ?? '');
            setParsedUserIds(
              rawUserIds.length > 0 && !isValidUUID(String(rawUserIds[0]).trim()) ? rawUserIds.slice(1) : rawUserIds,
            );
          },
          error: (parseError) => setError(`Failed to parse CSV: ${parseError.message}`),
        });
      },
      error: (parseError) => setError(`Failed to parse CSV: ${parseError.message}`),
    });
  }, []);

  const run = useCallback(
    async (dryRun: boolean): Promise<Review> => {
      let total = EMPTY_REVIEW;
      let responseVoucherName = voucherName;
      const batches = Array.from({ length: Math.ceil(userIds.length / BATCH_SIZE) }, (_, index) =>
        userIds.slice(index * BATCH_SIZE, (index + 1) * BATCH_SIZE),
      );
      const batchId = uuidv4();

      for (let index = 0; index < batches.length; index += 1) {
        const response = await bulkDeleteMutation.mutateAsync({
          voucher_id: voucherId,
          voucher_users: batches[index].map((user_id) => ({ user_id })),
          dryRun,
          batchId,
          batchRunCountTotal: batches.length,
          batchRunCount: index + 1,
        });
        total = {
          requestedCount: total.requestedCount + batches[index].length,
          eligibleCount: total.eligibleCount + response.result.willDelete.length,
          skippedCount: total.skippedCount + response.result.skipped.length,
          deletedCount: total.deletedCount + response.result.deletedVoucherUsers.length,
        };
        setResults((current) => current.concat(responseRows(response, dryRun)));
        responseVoucherName = response.result.voucherName;
        setProgress(Math.round(((index + 1) / batches.length) * 100));
      }

      return { ...total, voucherName: responseVoucherName };
    },
    [bulkDeleteMutation, userIds, voucherId, voucherName],
  );

  const handleReview = useCallback(async () => {
    if (userIds.length === 0) {
      setError('Upload a CSV file with at least one valid user ID.');
      return;
    }
    setError('');
    setProgress(0);
    setResults([]);
    setPaginationModel((current) => ({ ...current, page: 0 }));
    setIsRunning(true);
    try {
      setReview(await run(true));
    } catch (requestError) {
      setError(`Could not review deletion: ${(requestError as Error).message}`);
    } finally {
      setProgress(null);
      setIsRunning(false);
    }
  }, [run, userIds.length]);

  const handleConfirm = useCallback(async () => {
    setError('');
    setProgress(0);
    setResults([]);
    setPaginationModel((current) => ({ ...current, page: 0 }));
    setIsRunning(true);
    try {
      const result = await run(false);
      setCompleted(result);
      enqueueSnackbar(
        `Deleted ${formatCount(result.deletedCount)} voucher user${result.deletedCount === 1 ? '' : 's'}`,
        {
          variant: 'success',
        },
      );
      queryClient.invalidateQueries({ queryKey: ['voucher-users', voucherId] });
      queryClient.invalidateQueries({ queryKey: allVoucherUsersQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['voucher', voucherId] });
    } catch (requestError) {
      setError(`Deletion failed: ${(requestError as Error).message}`);
    } finally {
      setProgress(null);
      setIsRunning(false);
    }
  }, [enqueueSnackbar, queryClient, run, voucherId]);

  const summary = completed ?? review;
  const isBusy = isRunning;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk delete voucher users from CSV</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {!review && !completed && (
            <>
              <Typography variant="body2" color="text.secondary">
                Upload the same user-ID CSV accepted by bulk upload. The file is checked locally, then queued in serial
                200-user batches. Each returned user result is shown below.
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Expected CSV: a <code>user_id</code>, <code>userId</code>, <code>customer_id</code>, or{' '}
                  <code>customerId</code> column; headerless single-column files are also accepted.
                </Typography>
                <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} disabled={isBusy}>
                  Choose CSV
                  <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={handleFileChange} />
                </Button>
                {fileName && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {fileName}: {formatCount(userIds.length)} valid ID{userIds.length === 1 ? '' : 's'}
                    {duplicateCount > 0 ? `, ${formatCount(duplicateCount)} duplicate row(s) ignored` : ''}
                    {invalidCount > 0 ? `, ${formatCount(invalidCount)} invalid ID(s) ignored` : ''}.
                  </Typography>
                )}
              </Paper>
            </>
          )}

          {summary && (
            <>
              <Alert severity={completed ? 'success' : 'warning'}>
                {completed
                  ? `Deleted ${formatCount(summary.deletedCount)} voucher users from “${summary.voucherName}”.`
                  : `Review: ${formatCount(summary.eligibleCount)} of ${formatCount(summary.requestedCount)} submitted users will be deleted from “${summary.voucherName}”.`}
              </Alert>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">
                  {completed ? 'Deleted' : 'Eligible'}:{' '}
                  {formatCount(completed ? summary.deletedCount : summary.eligibleCount)}
                </Typography>
                <Typography variant="body2">Skipped: {formatCount(summary.skippedCount)}</Typography>
              </Paper>
              {!completed && (
                <Typography variant="body2" color="text.secondary">
                  Confirmation rechecks eligibility. Users whose assignment changes after this review may not be
                  deleted.
                </Typography>
              )}
            </>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {progress !== null && <LinearProgress variant="determinate" value={progress} />}
          {results.length > 0 && (
            <Paper variant="outlined" sx={{ height: 440, p: 1 }}>
              <Typography variant="subtitle2" sx={{ px: 1, pt: 0.5 }}>
                Results ({formatCount(results.length)})
              </Typography>
              <DataGrid
                rows={results}
                columns={RESULT_COLUMNS}
                pagination
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
                disableColumnFilter
                disableColumnMenu
                sx={{ border: 0, mt: 1 }}
              />
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleClose} disabled={isBusy}>
          {completed ? 'Close' : 'Cancel'}
        </Button>
        {!review && !completed && (
          <Button variant="contained" onClick={handleReview} disabled={isBusy || userIds.length === 0}>
            Review deletion
          </Button>
        )}
        {review && !completed && (
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirm}
            disabled={isBusy || review.eligibleCount === 0}
          >
            Confirm deletion
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
